import {
  getServerActionErrorMessage,
  ServerActionError,
} from "@/lib/action-utils";

class ApiClient {
  private readonly endpoint: string;
  private readonly headers: HeadersInit;
  private readonly defaultTimeout = 5000;

  constructor(endpoint: string, token: string) {
    this.endpoint = endpoint;
    this.headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async fetch<T>(
    path: string,
    options?: RequestInit,
    retries = 2,
    delay = 500,
    timeoutMs = this.defaultTimeout
  ): Promise<T> {
    for (let attempt = 0; attempt <= retries; attempt++) {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), timeoutMs);

      try {
        const response = await fetch(`${this.endpoint}${path}`, {
          ...options,
          headers: {
            ...this.headers,
            ...options?.headers,
          },
          signal: controller.signal,
        });

        clearTimeout(timeout);

        const text = await response.text();

        if (!response.ok) {
          throw new ServerActionError(
            getServerActionErrorMessage(
              text,
              `Fetch request failed: ${response.statusText}`
            )
          );
        }

        return text ? (JSON.parse(text) as T) : ({} as T);
      } catch (error: any) {
        clearTimeout(timeout);

        const isAbortError = error?.name === "AbortError";
        const isNetworkError =
          error instanceof TypeError ||
          error?.code === "ECONNREFUSED" ||
          error?.code === "UND_ERR_CONNECT_TIMEOUT" ||
          error?.message?.includes("fetch failed");

        if (attempt === retries || (!isAbortError && !isNetworkError)) {
          throw error;
        }

        await new Promise((res) => setTimeout(res, delay));
      }
    }

    throw new ServerActionError("Fetch request failed after retries");
  }

  post<T>(path: string, body: any) {
    return this.fetch<T>(path, {
      method: "POST",
      body: JSON.stringify(body),
    });
  }

  put<T>(path: string, body: any) {
    return this.fetch<T>(path, {
      method: "PUT",
      body: JSON.stringify(body),
    });
  }

  delete<T>(path: string) {
    return this.fetch<T>(path, { method: "DELETE" });
  }
}

export { ApiClient };
